<template>
    <Card pageTitle="Purchase Order Detail">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_PURCHASE_ORDER">
            <div>
                <div class="mb-3 d-flex justify-content-between items-center">
                    <div class="">
                        <Button noMargin @click="$router.push('/purchase-orders')">
                            <i class="fas fa-chevron-left"></i> Back To Purchase Orders
                        </Button>
                    </div>
                    <div class="d-flex" style="gap: 12px" v-if="purchaseOrder && !loading">
                        <Button v-if="canEditOrder && !editMode" noMargin borderOnly type="button" @click="startEditing">
                            Edit Purchase Order
                        </Button>
                        <template v-if="editMode">
                            <Button noMargin borderOnly type="button" :disabled="savingOrder" @click="discardEdits">
                                Discard Changes
                            </Button>
                            <Button noMargin type="button" :disabled="savingOrder" @click="saveEdits">
                                {{ savingOrder ? "Saving..." : "Save Changes" }}
                            </Button>
                        </template>
                        <template v-else>
                            <Button
                                noMargin
                                :disabled="orderDisabled"
                                :disableOnClick="true"
                                variant="danger"
                                type="button"
                                @click="cancelOrder"
                            >
                                {{ cancellingOrder ? "Please wait..." : "Cancel" }}
                            </Button>
                            <Button :disabled="orderDisabled" :disableOnClick="true" noMargin type="button" @click="markComplete">
                                {{ completingOrder ? "Please wait..." : "Mark As Complete" }}
                            </Button>
                        </template>
                    </div>
                </div>

                <div v-if="editMode" class="edit-banner" role="status">
                    <i class="fas fa-pen" aria-hidden="true"></i>
                    <div>
                        <strong>Editing submitted purchase order</strong>
                        <span>Received quantities and received unit costs will not be changed.</span>
                    </div>
                </div>

                <DetailContainer :title="`Purchase Order ${purchaseOrder ? '#' + purchaseOrder.name : ''}`">
                    <div v-if="loading">Loading...</div>
                    <div v-if="purchaseOrder">
                        <div class="row">
                            <div class="col-md-6">
                                <div>
                                    <InfoListItem
                                        title="Status"
                                        :value="purchaseOrder.completed ? 'Completed' : purchaseOrder.cancelled ? 'Cancelled' : 'Submitted'"
                                    />
                                </div>
                                <div><InfoListItem title="Supplier" :value="purchaseOrder.supplier_name" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-supplier-invoice">Supplier Invoice No.</label>
                                    <input id="po-supplier-invoice" v-model.trim="editForm.supplier_invoice_no" type="text" />
                                </div>
                                <div v-else><InfoListItem title="Supplier Invoice No." :value="purchaseOrder.supplier_invoice_no || 'N/A'" /></div>
                                <div><InfoListItem title="Target/To Warehouse" :value="purchaseOrder.target_warehouse_address" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-shipping-method">Shipping Method</label>
                                    <input id="po-shipping-method" v-model.trim="editForm.shipping_method" type="text" />
                                </div>
                                <div v-else><InfoListItem title="Shipping Method" :value="purchaseOrder.shipping_method || 'N/A'" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-tracking-number">Tracking Number</label>
                                    <input id="po-tracking-number" v-model.trim="editForm.tracking_number" type="text" />
                                </div>
                                <div v-else><InfoListItem title="Tracking Number" :value="purchaseOrder.tracking_number || 'N/A'" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-eta">ETA</label>
                                    <input id="po-eta" v-model="editForm.eta" type="date" />
                                </div>
                                <div v-else><InfoListItem title="ETA" :value="purchaseOrder.etaFormatted || 'N/A'" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-notes">Notes</label>
                                    <textarea id="po-notes" v-model.trim="editForm.notes" rows="3"></textarea>
                                </div>
                                <div v-else><InfoListItem title="Notes" :value="purchaseOrder.notes || 'N/A'" /></div>
                            </div>
                            <div class="col-md-6">
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-bill-to">Bill To Address</label>
                                    <textarea id="po-bill-to" v-model.trim="editForm.bill_to_address" rows="3"></textarea>
                                </div>
                                <div v-else><InfoListItem title="Bill To Address" :value="purchaseOrder.bill_to_address || 'N/A'" /></div>
                                <div v-if="editMode" class="edit-field">
                                    <label for="po-ship-to">Ship To Address</label>
                                    <textarea id="po-ship-to" v-model.trim="editForm.ship_to_address" rows="3"></textarea>
                                </div>
                                <div v-else><InfoListItem title="Ship To Address" :value="purchaseOrder.ship_to_address || 'N/A'" /></div>
                                <div><InfoListItem title="Created On" :value="purchaseOrder.createdAtFormatted1" /></div>
                                <div><InfoListItem title="Submitted On" :value="purchaseOrder.submittedAt || 'N/A'" /></div>
                                <div><InfoListItem title="Last Received On" :value="purchaseOrder.lastReceivedAt || 'N/A'" /></div>
                                <div><InfoListItem title="Completed On" :value="purchaseOrder.completedAt || 'N/A'" /></div>
                                <div><InfoListItem title="Created By" :value="purchaseOrder.createdByUser ? purchaseOrder.createdByUser.full_name || purchaseOrder.createdByUser.name : 'N/A'" /></div>
                                <div><InfoListItem title="Received By" :value="purchaseOrder.receivedByUser ? purchaseOrder.receivedByUser.full_name || purchaseOrder.receivedByUser.name : 'N/A'" /></div>
                            </div>
                        </div>

                        <!-- Total Summary Section -->
                        <div class="row mt-4">
                            <div class="col-md-12">
                                <div class="card">
                                    <div class="card-header">
                                        <h5>Order Summary</h5>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <div class="col-md-6">
                                                <InfoListItem title="Subtotal" :value="`$${displaySubtotal.toFixed(2)}`" />
                                                <div v-if="editMode" class="edit-field edit-field--money">
                                                    <label for="po-shipping-cost">Shipping Cost</label>
                                                    <div class="money-input"><span>$</span><input id="po-shipping-cost" v-model="editForm.shipping_cost" type="number" min="0" step="0.01" /></div>
                                                </div>
                                                <InfoListItem v-else title="Shipping Cost" :value="`$${purchaseOrder.shipping_cost ? Number(purchaseOrder.shipping_cost).toFixed(2) : '0.00'}`" />
                                            </div>
                                            <div class="col-md-6">
                                                <div v-if="editMode" class="edit-field edit-field--money">
                                                    <label for="po-tax">Tax</label>
                                                    <div class="money-input"><span>$</span><input id="po-tax" v-model="editForm.tax" type="number" min="0" step="0.01" /></div>
                                                </div>
                                                <InfoListItem v-else title="Tax" :value="`$${purchaseOrder.tax ? Number(purchaseOrder.tax).toFixed(2) : '0.00'}`" />
                                                <InfoListItem title="Grand Total" :value="`$${displayGrandTotal.toFixed(2)}`" style="font-weight: bold;" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DetailContainer>

                <div v-if="purchaseOrder">
                    <div class="my-3">
                        <div class="d-flex justify-content-between items-center">
                            <SectionTitle>Products</SectionTitle>
                            <router-link v-if="!orderDisabled && !editMode" :to="`/purchase-orders/${purchaseOrder.name}/receive`">
                                <Button>Receive Products</Button>
                            </router-link>
                        </div>

                        <CustomTable v-if="purchaseOrderItems.length">
                            <template #head>
                                <tr>
                                    <th>Item Name</th>
                                    <th>Supplier SKU</th>
                                    <th>Catalog SKU</th>
                                    <th>Qty Ordered</th>
                                    <th>Ordered Unit Cost</th>
                                    <th>Expected Total</th>
                                    <th>Qty Received</th>
                                    <th>Received Unit Cost</th>
                                    <th>Received Total</th>
                                </tr>
                            </template>
                            <template #body>
                                <tr v-for="poItem of purchaseOrderItems" :key="poItem.id">
                                    <td>{{ poItem.item_name }}</td>
                                    <td>{{ poItem.sku }}</td>
                                    <td>{{ poItem.catalog_sku }}</td>
                                    <td>{{ poItem.shipped_qty }}</td>
                                    <td>
                                        <div v-if="editMode" class="money-input money-input--table">
                                            <span>$</span>
                                            <input
                                                v-model="editItemPrices[poItem.id]"
                                                :aria-label="`Ordered unit cost for ${poItem.item_name}`"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                            />
                                        </div>
                                        <template v-else>${{ Number(poItem.unit_price || 0).toFixed(2) }}</template>
                                    </td>
                                    <td>${{ expectedLineTotal(poItem).toFixed(2) }}</td>
                                    <td>{{ poItem.shipped_qty_received }}</td>
                                    <td>${{ Number(poItem.unit_price_received || 0).toFixed(2) }}</td>
                                    <td>${{ (Number(poItem.shipped_qty_received || 0) * Number(poItem.unit_price_received || 0)).toFixed(2) }}</td>
                                </tr>
                            </template>
                        </CustomTable>
                        <div v-if="!purchaseOrderItems.length">No Purchase Order Items.</div>
                    </div>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>
<script>
import Button from "../../components/UI/Button.vue"
import { confirmAction, errMsg } from "../../utils/common"
import { mapState } from "vuex"
import { http } from "@/utils/http"
import Card from "../../components/UI/Card.vue"
import InfoListItem from "../../components/UI/InfoListItem.vue"
import CustomTable from "../../components/layout/CustomTable.vue"
import { usDateFormat } from "../../utils/common"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import SectionTitle from "../../components/UI/SectionTitle.vue"
import DetailContainer from "../../components/layout/DetailContainer.vue"

export default {
    data() {
        return {
            editMode: false,
            loading: true,
            PERMISSION_CODES,
            purchaseOrder: null,
            purchaseOrderItems: [],
            cancellingOrder: false,
            completingOrder: false,
            savingOrder: false,
            editForm: {
                supplier_invoice_no: "",
                tax: 0,
                shipping_cost: 0,
                shipping_method: "",
                tracking_number: "",
                notes: "",
                eta: "",
                bill_to_address: "",
                ship_to_address: "",
            },
            editItemPrices: {},
        }
    },
    async mounted() {
        await this.fetchData()
        this.loading = false
    },
    methods: {
        usDateFormat,
        dateInputValue(value) {
            if (!value) return ""
            const datePart = String(value).match(/^\d{4}-\d{2}-\d{2}/)
            if (datePart) return datePart[0]

            const parsed = new Date(value)
            return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10)
        },
        startEditing() {
            if (!this.canEditOrder) return

            this.editForm = {
                supplier_invoice_no: this.purchaseOrder.supplier_invoice_no || "",
                tax: Number(this.purchaseOrder.tax || 0),
                shipping_cost: Number(this.purchaseOrder.shipping_cost || 0),
                shipping_method: this.purchaseOrder.shipping_method || "",
                tracking_number: this.purchaseOrder.tracking_number || "",
                notes: this.purchaseOrder.notes || "",
                eta: this.dateInputValue(this.purchaseOrder.eta),
                bill_to_address: this.purchaseOrder.bill_to_address || "",
                ship_to_address: this.purchaseOrder.ship_to_address || "",
            }
            this.editItemPrices = this.purchaseOrderItems.reduce((prices, item) => {
                prices[item.id] = Number(item.unit_price || 0)
                return prices
            }, {})
            this.editMode = true
        },
        discardEdits() {
            if (this.savingOrder) return
            this.editMode = false
            this.editItemPrices = {}
        },
        moneyValue(value, label) {
            const parsed = Number(value)
            if (value === "" || value === null || value === undefined || !Number.isFinite(parsed) || parsed < 0) {
                throw new Error(`${label} must be zero or a positive number.`)
            }
            return parsed
        },
        expectedLineTotal(item) {
            const price = this.editMode ? Number(this.editItemPrices[item.id]) : Number(item.unit_price || 0)
            return Number(item.shipped_qty || 0) * (Number.isFinite(price) ? price : 0)
        },
        async saveEdits() {
            if (!this.canEditOrder || this.savingOrder) return

            try {
                const tax = this.moneyValue(this.editForm.tax, "Tax")
                const shippingCost = this.moneyValue(this.editForm.shipping_cost, "Shipping cost")
                const updateItems = this.purchaseOrderItems.map((item) => ({
                    item_id: item.id,
                    unit_price: this.moneyValue(this.editItemPrices[item.id], `Ordered unit cost for ${item.item_name}`),
                }))

                this.savingOrder = true
                await http.patch(`/purchase-orders/${this.purchaseOrder.id}`, {
                    supplier_invoice_no: this.editForm.supplier_invoice_no,
                    tax,
                    shipping_cost: shippingCost,
                    shipping_method: this.editForm.shipping_method,
                    tracking_number: this.editForm.tracking_number,
                    notes: this.editForm.notes,
                    eta: this.editForm.eta || null,
                    bill_to_address: this.editForm.bill_to_address,
                    ship_to_address: this.editForm.ship_to_address,
                    updateItems,
                })
                this.$toast.success("Purchase order updated successfully!")
                this.editMode = false
                this.editItemPrices = {}
                await this.fetchData()
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update this purchase order."))
            } finally {
                this.savingOrder = false
            }
        },
        async fetchData() {
            try {
                this.loading = true
                const res = await http.get(`/purchase-orders/${this.$route.params && this.$route.params.id}`)
                if (res.data.result) {
                    this.purchaseOrder = res.data.result.purchaseOrder
                    this.purchaseOrderItems = res.data.result.purchaseOrderItems || []
                }
            } catch (err) {
                console.log("error in fetching user init data", err)
            }
            this.loading = false
        },
        async cancelOrder() {
            try {
                await confirmAction(
                    "Cancelled",
                    "Purchase Order cancelled!",
                    async () => {
                        this.cancellingOrder = true
                        await this.updateOrderStatus(0, 1)
                        this.cancellingOrder = false
                    },
                    "Are you sure to cancel this Purchase Order?"
                )
            } catch (err) {}
        },
        async markComplete() {
            try {
                await confirmAction(
                    "Completed",
                    "Purchase Order marked complete!",
                    async () => {
                        this.completingOrder = true
                        await this.updateOrderStatus(1, 0)
                        this.completingOrder = false
                    },
                    "Are you sure to complete this Purchase Order?"
                )
            } catch (err) {}
        },
        async updateOrderStatus(completed, cancelled) {
            try {
                const res = await http.patch(`/purchase-orders/${this.purchaseOrder.id}/status`, {
                    completed,
                    cancelled,
                })
                this.fetchData()
            } catch (err) {
                console.log("error in updating order status", err)
            }
        },
    },
    components: { DetailContainer, SectionTitle, AllowPermission, CustomTable, Card, Button, InfoListItem },
    computed: {
        ...mapState("misc", ["showSidebar", "countries"]),
        ...mapState("auth", ["user"]),
        canEditOrder() {
            return Boolean(
                this.purchaseOrder &&
                    !this.purchaseOrder.completed &&
                    !this.purchaseOrder.cancelled &&
                    checkHasPermission({
                        isAdmin: Boolean(this.user && this.user.isAdmin),
                        permissionId: PERMISSION_CODES.CMS_EDIT_PURCHASE_ORDER,
                        userPermissionIds: (this.user && this.user.permissionIds) || [],
                    })
            )
        },
        displaySubtotal() {
            if (!this.editMode) return Number((this.purchaseOrder && this.purchaseOrder.subtotal) || 0)
            return this.purchaseOrderItems.reduce((total, item) => total + this.expectedLineTotal(item), 0)
        },
        displayGrandTotal() {
            if (!this.editMode) return Number((this.purchaseOrder && this.purchaseOrder.grandTotal) || 0)
            const tax = Number(this.editForm.tax)
            const shippingCost = Number(this.editForm.shipping_cost)
            return this.displaySubtotal + (Number.isFinite(tax) ? tax : 0) + (Number.isFinite(shippingCost) ? shippingCost : 0)
        },
        orderDisabled() {
            return (
                this.cancellingOrder ||
                this.completingOrder ||
                this.savingOrder ||
                (this.purchaseOrder && this.purchaseOrder.completed) ||
                (this.purchaseOrder && this.purchaseOrder.cancelled)
            )
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.mb-3.d-flex {
  gap: $sectionGap;
}

.edit-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: $sectionGap;
  color: #173c37;
  background: #edf7f4;
  border: 1px solid #bddfd5;
  border-radius: 8px;

  i {
    margin-top: 3px;
  }

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 2px;
    color: #48645f;
    font-size: 0.875rem;
  }
}

.edit-field {
  margin-bottom: 14px;

  label {
    display: block;
    margin-bottom: 5px;
    color: #4d5a57;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  input,
  textarea {
    width: 100%;
    padding: 8px 10px;
    color: #1e2927;
    background: #fff;
    border: 1px solid #b8c4c1;
    border-radius: 5px;
  }

  textarea {
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    border-color: #176b5e;
    box-shadow: 0 0 0 3px rgba(23, 107, 94, 0.14);
    outline: none;
  }
}

.money-input {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  background: #fff;
  border: 1px solid #b8c4c1;
  border-radius: 5px;

  span {
    padding-left: 10px;
    color: #5c6b68;
  }

  input {
    min-width: 0;
    border: 0;
    box-shadow: none;
  }

  &:focus-within {
    border-color: #176b5e;
    box-shadow: 0 0 0 3px rgba(23, 107, 94, 0.14);
  }
}

.money-input--table {
  min-width: 112px;

  input {
    width: 84px;
    padding: 7px 8px;
    text-align: right;
    background: transparent;
    outline: none;
  }
}

/* Right-align numeric columns in product table */
::deep(table) {
  thead th:nth-child(4),
  thead th:nth-child(5),
  thead th:nth-child(6),
  thead th:nth-child(7),
  thead th:nth-child(8),
  thead th:nth-child(9) {
    text-align: right;
  }
  tbody td:nth-child(4),
  tbody td:nth-child(5),
  tbody td:nth-child(6),
  tbody td:nth-child(7),
  tbody td:nth-child(8),
  tbody td:nth-child(9) {
    text-align: right;
  }
}
</style>
